import type { NavIcon } from './AppShell'

/**
 * Secondary navigation: which part of this area am I looking at.
 *
 * **One level, never two.** The sidebar says which business area you are in and
 * this says which screen inside it, which is the split that let the sidebar go
 * from ten entries to eight without hiding anything. A tab is a real
 * destination with its own URL, so every link, bookmark and browser Back still
 * works exactly as it did.
 *
 * **Touch first.** 44px on a phone, where a finger is the pointer, and tighter
 * on a desktop where a mouse is. The strip is the only thing that scrolls
 * sideways when there is not room, never the page, and the active tab is
 * scrolled into view so a reader never lands on a strip that appears to start
 * somewhere else.
 *
 * The strip is named "<area> pages" rather than "sections", because the
 * sidebar landmark is already called Sections and two navigation landmarks with
 * the same name is a coin toss for anybody moving by landmark.
 *
 * **Not colour alone.** The current tab carries a weight change and a rule
 * under it as well as the accent, so it survives greyscale and a reader who
 * cannot separate the tints.
 */

export interface SectionTabItem {
  readonly id: string
  readonly label: string
  readonly path: string
  readonly icon?: NavIcon
}

export default function SectionTabs({
  label,
  items,
  activeId,
  onNavigate,
}: {
  /** Names the area, for a reader who arrives by screen reader rather than by eye. */
  readonly label: string
  readonly items: readonly SectionTabItem[]
  readonly activeId: string
  readonly onNavigate: (path: string) => void
}) {
  if (items.length < 2) return null

  return (
    <nav
      aria-label={`${label} pages`}
      data-testid="section-tabs"
      className="mb-5 border-b border-line"
    >
      {/*
        **It wraps on a desktop and scrolls on a phone.** Settings holds twelve
        screens, and a single scrolling row cut the last three off at the window
        edge with nothing to say they were there. There is room to wrap where
        there is width, and a phone has none, so below `sm` it scrolls with
        `scroll-px` keeping the first and last tab clear of the edge and
        `scrollbar-none` hiding the bar itself.
      */}
      <div className="-mb-px flex gap-1 overflow-x-auto scroll-px-4 scrollbar-none sm:flex-wrap sm:overflow-visible">
        {items.map((item) => {
          const current = item.id === activeId
          const Icon = item.icon
          return (
            <button
              key={item.id}
              type="button"
              aria-current={current ? 'page' : undefined}
              data-testid={`section-tab-${item.id}`}
              ref={(node) => {
                /*
                 * Bringing the current tab into view is a convenience, so it is
                 * feature-detected rather than assumed. `scrollIntoView` is
                 * absent in jsdom and in some embedded browsers, and a strip
                 * that throws when it cannot scroll is worse than one that does
                 * not scroll.
                 */
                if (current && node !== null && typeof node.scrollIntoView === 'function') {
                  node.scrollIntoView({ block: 'nearest', inline: 'nearest' })
                }
              }}
              onClick={() => onNavigate(item.path)}
              className={`inline-flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 text-[0.875rem] transition-colors sm:min-h-0 sm:py-2.5 ${
                current
                  ? 'border-accent font-semibold text-accent-strong'
                  : 'border-transparent font-medium text-ink-muted hover:border-line hover:text-ink'
              }`}
            >
              {Icon !== undefined && <Icon aria-hidden="true" className="h-4 w-4" />}
              {item.label}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
